import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Save, Mail, Phone, Building2, Calendar, ShoppingBag, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAdminAuth } from "@/context/AdminAuthContext";

const STATUS_ORDER = ["new", "contacted", "quote_sent", "won", "closed"];
const STATUS_LABELS = {
  new: "New",
  contacted: "Contacted",
  quote_sent: "Quote Sent",
  won: "Won",
  closed: "Closed",
};

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-US", {
      weekday: "short", month: "short", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const Field = ({ label, value, mono }) => (
  <div className="border-b border-white/5 py-3">
    <p className="text-[10px] uppercase tracking-[0.22em] text-white/40">{label}</p>
    <p className={`mt-1 text-white ${mono ? "font-mono text-xs" : "text-sm"}`}>{value || <span className="text-white/30">-</span>}</p>
  </div>
);

const AdminQuoteDetail = () => {
  const { type, id } = useParams(); // type: 'q' | 'c'
  const { authHeader, API, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("new");
  const [saving, setSaving] = useState(false);

  const isQuote = type === "q";
  const endpoint = isQuote ? "quotes" : "contacts";

  useEffect(() => {
    document.title = "Enquiry Detail | Hospitality Hub Admin";
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/admin/${endpoint}/${id}`, { headers: authHeader() });
        if (cancelled) return;
        setItem(data);
        setNotes(data.admin_notes || "");
        setStatus(data.status || "new");
      } catch (err) {
        if (err?.response?.status === 401) {
          logout();
          navigate("/admin/login", { replace: true });
          return;
        }
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, endpoint]);

  const save = async (payload) => {
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/admin/${endpoint}/${id}`, payload, { headers: authHeader() });
      setItem(data);
      setStatus(data.status);
      setNotes(data.admin_notes || "");
      toast.success("Saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onStatusChange = (s) => {
    setStatus(s);
    save({ status: s });
  };

  const onSaveNotes = () => save({ admin_notes: notes });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0E0E0E] text-white">
        <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Loading…</p>
      </div>
    );
  }
  if (notFound || !item) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#0E0E0E] text-white gap-4">
        <p className="text-xs uppercase tracking-[0.3em] text-red-400">Enquiry not found</p>
        <Link to="/admin/quotes" className="text-[#D4AF37] text-xs uppercase tracking-[0.2em]">← Back to inbox</Link>
      </div>
    );
  }

  return (
    <main data-testid="admin-detail-page" className="min-h-screen bg-[#0E0E0E] text-white">
      <header className="border-b border-white/10 bg-[#0E0E0E] sticky top-0 z-20">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-4">
          <button
            data-testid="admin-detail-back"
            onClick={() => navigate("/admin/quotes")}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-white/70 hover:text-[#D4AF37] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Inbox
          </button>
          <div className="flex items-center gap-3">
            <p className="text-[10px] uppercase tracking-[0.24em] text-[#D4AF37]">{isQuote ? "Quote Request" : "Contact Enquiry"}</p>
          </div>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Left: details */}
        <div className="lg:col-span-8">
          <p className="text-[10px] uppercase tracking-[0.28em] text-white/40 mb-2">
            <Calendar className="w-3 h-3 inline -mt-0.5 mr-1.5 text-[#D4AF37]" />
            {formatDate(item.created_at)}
          </p>
          <h1 data-testid="detail-title" className="font-serif-display text-3xl md:text-4xl leading-tight">
            {item.full_name || "Anonymous"}
          </h1>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8">
            <Field label="Company / Hotel" value={item.hotel_name} />
            <Field label="Email" value={item.email} />
            <Field label="Phone" value={item.phone} />
            <Field label="Enquiry ID" value={item.id} mono />
            {!isQuote && <Field label="Subject" value={item.subject} />}
          </div>

          {item.message && (
            <div className="mt-8">
              <p className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-3">Customer Message</p>
              <div className="border border-white/10 bg-white/[0.03] p-5 text-sm text-white/85 leading-relaxed whitespace-pre-wrap">
                {item.message}
              </div>
            </div>
          )}

          {isQuote && item.items && item.items.length > 0 && (
            <div className="mt-10">
              <p className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-4 flex items-center gap-2">
                <ShoppingBag className="w-3.5 h-3.5 text-[#D4AF37]" />
                Requested Products ({item.items.length})
              </p>
              <div className="border border-white/10 divide-y divide-white/5">
                {item.items.map((it, idx) => (
                  <div key={idx} data-testid={`detail-item-${idx}`} className="flex gap-4 p-4 items-start">
                    {it.image && (
                      <div className="w-16 h-16 bg-white/5 flex items-center justify-center overflow-hidden shrink-0">
                        <img src={it.image} alt={it.name} className="w-full h-full object-contain p-1" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm leading-snug">{it.name}</p>
                      <p className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] mt-1">{it.category}</p>
                      <p className="text-xs text-white/40 mt-1 font-mono">{it.product_id}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[10px] uppercase tracking-[0.18em] text-white/40">Qty</p>
                      <p className="font-serif-display text-2xl text-white">{it.quantity || 1}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Contact links */}
          <div className="mt-10 flex flex-wrap gap-3">
            {item.email && (
              <a
                href={`mailto:${item.email}?subject=${encodeURIComponent(isQuote ? "Your Hospitality Hub Quote" : "Re: your enquiry")}`}
                data-testid="detail-email-btn"
                className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#B5952F] text-white px-6 py-3 uppercase tracking-[0.2em] text-xs transition-colors"
              >
                <Mail className="w-3.5 h-3.5" /> Email Customer
              </a>
            )}
            {item.phone && (
              <a
                href={`tel:${item.phone}`}
                data-testid="detail-call-btn"
                className="inline-flex items-center gap-2 border border-white/30 hover:bg-white hover:text-[#0E0E0E] text-white px-6 py-3 uppercase tracking-[0.2em] text-xs transition-colors"
              >
                <Phone className="w-3.5 h-3.5" /> Call
              </a>
            )}
          </div>
        </div>

        {/* Right: status + private notes */}
        <aside className="lg:col-span-4 space-y-8">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-3">Status</p>
            <div className="grid grid-cols-1 gap-2">
              {STATUS_ORDER.map((s) => (
                <button
                  key={s}
                  data-testid={`detail-status-${s}`}
                  onClick={() => onStatusChange(s)}
                  disabled={saving}
                  className={`text-left px-4 py-3 border transition-colors text-xs uppercase tracking-[0.2em] ${
                    status === s
                      ? "bg-[#D4AF37] border-[#D4AF37] text-white"
                      : "bg-white/[0.03] border-white/10 hover:border-[#D4AF37]/60 text-white/70"
                  }`}
                >
                  {STATUS_LABELS[s]}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-3 flex items-center gap-2">
              <Lock className="w-3 h-3 text-[#D4AF37]" />
              Internal Notes · Private
            </p>
            <textarea
              data-testid="detail-notes-textarea"
              rows={8}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Only visible to admins. Not shown to the customer."
              className="w-full bg-white/[0.03] border border-white/10 focus:border-[#D4AF37] outline-none p-3 text-sm placeholder:text-white/25 resize-y"
            />
            <button
              data-testid="detail-notes-save"
              onClick={onSaveNotes}
              disabled={saving}
              className="mt-3 inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#B5952F] disabled:opacity-50 text-white px-5 py-2.5 uppercase tracking-[0.2em] text-[10px] transition-colors"
            >
              <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save Notes"}
            </button>
          </div>

          <div className="border-t border-white/10 pt-6 text-[10px] uppercase tracking-[0.18em] text-white/30 space-y-1.5">
            <p><Building2 className="w-3 h-3 inline mr-1 text-white/30" /> Source: {isQuote ? "Request-a-Quote form" : "Contact form"}</p>
            <p>Enquiry ID: <span className="font-mono normal-case">{item.id.slice(0, 8)}…</span></p>
          </div>
        </aside>
      </div>
    </main>
  );
};

export default AdminQuoteDetail;
